import { queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  MessageSquare,
  Mic,
  NotebookPen,
  ThumbsDown,
  ThumbsUp,
  TrendingDown,
  TrendingUp
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FeedbackList } from '~/components/features/admin/feedback-list';
import { PageContainer } from '~/components/layouts/page-container';
import { useDocumentTitle } from '~/hooks/use-document-title';
import { api } from '~/lib/api';
import { cn } from '~/lib/utils';

type ThumbsFilter = 'all' | 'up' | 'down';
type ModalityFilter = 'all' | 'text' | 'voice';
type HasNotesFilter = 'all' | 'yes';

const feedbackQueryOptions = (
  page: number,
  thumbs: ThumbsFilter,
  modality: ModalityFilter,
  hasNotes: HasNotesFilter
) =>
  queryOptions({
    queryKey: ['admin', 'feedback', page, thumbs, modality, hasNotes] as const,
    queryFn: async () => {
      const query: Record<string, string> = { page: String(page) };
      if (thumbs !== 'all') query['thumbs'] = thumbs;
      if (modality !== 'all') query['modality'] = modality;
      if (hasNotes === 'yes') query['hasNotes'] = 'true';
      const res = await api.api.admin.feedback.$get({ query });
      if (!res.ok) throw new Error('Failed to fetch feedback');
      return res.json();
    }
  });

const feedbackStatsQueryOptions = queryOptions({
  queryKey: ['admin', 'feedback', 'stats'] as const,
  queryFn: async () => {
    const res = await api.api.admin.feedback.stats.$get();
    if (!res.ok) throw new Error('Failed to fetch feedback stats');
    return res.json();
  }
});

export const Route = createFileRoute('/_authenticated/admin/feedback/')({
  validateSearch: z.object({
    page: z.number().int().min(1).default(1),
    thumbs: z.enum(['all', 'up', 'down']).default('all'),
    modality: z.enum(['all', 'text', 'voice']).default('all'),
    hasNotes: z.enum(['all', 'yes']).default('all')
  }),
  loaderDeps: ({ search }) => ({
    page: search.page,
    thumbs: search.thumbs,
    modality: search.modality,
    hasNotes: search.hasNotes
  }),
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureQueryData(
        feedbackQueryOptions(deps.page, deps.thumbs, deps.modality, deps.hasNotes)
      ),
      context.queryClient.ensureQueryData(feedbackStatsQueryOptions)
    ]),
  component: FeedbackPage
});

function FilterGroup<T extends string>({
  value,
  options,
  onChange
}: {
  value: T;
  options: { value: T; label: string; icon?: React.ReactNode }[];
  onChange: (v: T) => void;
}): React.ReactNode {
  return (
    <div className="flex gap-1 rounded-lg border border-border p-1">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => {
            onChange(opt.value);
          }}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            value === opt.value
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {opt.icon}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function WowBadge({
  total,
  bad,
  prevTotal,
  prevBad
}: {
  total: number;
  bad: number;
  prevTotal: number;
  prevBad: number;
}): React.ReactNode {
  const { t } = useTranslation();
  const rate = total > 0 ? Math.round((bad / total) * 100) : null;
  const prevRate = prevTotal > 0 ? Math.round((prevBad / prevTotal) * 100) : null;
  const delta = rate !== null && prevRate !== null ? rate - prevRate : null;

  if (prevTotal === 0) {
    return (
      <span className="text-xs text-muted-foreground">{t('admin.feedback.stats.wowNoPrev')}</span>
    );
  }
  if (delta === null || delta === 0) {
    return (
      <span className="text-xs text-muted-foreground">{t('admin.feedback.stats.wowFlat')}</span>
    );
  }
  if (delta > 0) {
    return (
      <span className="flex items-center gap-0.5 text-xs text-destructive">
        <TrendingUp className="size-3" />
        {t('admin.feedback.stats.wowUp', { delta })}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-0.5 text-xs text-green-600 dark:text-green-400">
      <TrendingDown className="size-3" />
      {t('admin.feedback.stats.wowDown', { delta: Math.abs(delta) })}
    </span>
  );
}

function BadRateColumn({
  icon,
  label,
  total,
  bad,
  prevTotal,
  prevBad
}: {
  icon: React.ReactNode;
  label: string;
  total: number;
  bad: number;
  prevTotal: number;
  prevBad: number;
}): React.ReactNode {
  const { t } = useTranslation();
  const rate = total > 0 ? Math.round((bad / total) * 100) : null;

  return (
    <div className="flex flex-col gap-0.5">
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        {icon}
        {label} ({String(total)})
      </span>
      <span className="flex items-baseline gap-1.5">
        <span className="text-xl font-semibold tabular-nums leading-tight">
          {rate !== null ? `${String(rate)}%` : t('admin.feedback.stats.noData')}
        </span>
        <WowBadge total={total} bad={bad} prevTotal={prevTotal} prevBad={prevBad} />
      </span>
    </div>
  );
}

function BadRateCard({
  chat,
  voice
}: {
  chat: { total: number; bad: number; prevTotal: number; prevBad: number };
  voice: { total: number; bad: number; prevTotal: number; prevBad: number };
}): React.ReactNode {
  const { t } = useTranslation();

  return (
    <div className="flex items-stretch gap-4 rounded-lg border border-border bg-card px-4 py-2.5">
      <div className="flex flex-col justify-center">
        <span className="text-xs font-medium text-muted-foreground">
          {t('admin.feedback.stats.title')}
        </span>
        <span className="text-xs text-muted-foreground">{t('admin.feedback.stats.period')}</span>
      </div>
      <div className="w-px bg-border" />
      <BadRateColumn
        icon={<MessageSquare className="size-3.5" />}
        label={t('admin.feedback.modality.text')}
        {...chat}
      />
      <div className="w-px bg-border" />
      <BadRateColumn
        icon={<Mic className="size-3.5" />}
        label={t('admin.feedback.modality.voice')}
        {...voice}
      />
    </div>
  );
}

function FeedbackPage(): React.ReactNode {
  const { t } = useTranslation();
  useDocumentTitle(t('admin.feedback.title'));

  const { page, thumbs, modality, hasNotes } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data } = useQuery(feedbackQueryOptions(page, thumbs, modality, hasNotes));
  const { data: stats } = useQuery(feedbackStatsQueryOptions);

  function handlePageChange(newPage: number): void {
    void navigate({ search: (prev) => ({ ...prev, page: newPage }) });
  }

  function handleThumbsFilter(value: ThumbsFilter): void {
    void navigate({ search: (prev) => ({ ...prev, page: 1, thumbs: value }) });
  }

  function handleModalityFilter(value: ModalityFilter): void {
    void navigate({ search: (prev) => ({ ...prev, page: 1, modality: value }) });
  }

  function handleHasNotesFilter(value: HasNotesFilter): void {
    void navigate({ search: (prev) => ({ ...prev, page: 1, hasNotes: value }) });
  }

  const feedback = data?.feedback ?? [];
  const total = data?.total ?? 0;
  const limit = data?.limit ?? 20;

  return (
    <PageContainer
      noScroll
      title={t('admin.feedback.title')}
      breadcrumbs={[{ label: t('admin.feedback.title') }]}
    >
      <div className="flex flex-1 min-h-0 flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            <FilterGroup<ThumbsFilter>
              value={thumbs}
              onChange={handleThumbsFilter}
              options={[
                { value: 'all', label: t('admin.feedback.filter.all') },
                {
                  value: 'down',
                  label: t('admin.feedback.filter.down'),
                  icon: <ThumbsDown className="size-3.5" />
                },
                {
                  value: 'up',
                  label: t('admin.feedback.filter.up'),
                  icon: <ThumbsUp className="size-3.5" />
                }
              ]}
            />
            <FilterGroup<ModalityFilter>
              value={modality}
              onChange={handleModalityFilter}
              options={[
                { value: 'all', label: t('admin.feedback.filter.all') },
                {
                  value: 'voice',
                  label: t('admin.feedback.modality.voice'),
                  icon: <Mic className="size-3.5" />
                },
                {
                  value: 'text',
                  label: t('admin.feedback.modality.text'),
                  icon: <MessageSquare className="size-3.5" />
                }
              ]}
            />
            <FilterGroup<HasNotesFilter>
              value={hasNotes}
              onChange={handleHasNotesFilter}
              options={[
                { value: 'all', label: t('admin.feedback.filter.all') },
                {
                  value: 'yes',
                  label: t('admin.feedback.filter.hasNotes'),
                  icon: <NotebookPen className="size-3.5" />
                }
              ]}
            />
          </div>
          {stats && <BadRateCard chat={stats.chat} voice={stats.voice} />}
        </div>

        <FeedbackList
          feedback={feedback}
          total={total}
          page={page}
          limit={limit}
          onPageChange={handlePageChange}
        />
      </div>
    </PageContainer>
  );
}
